import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Ticket as TicketIcon,
  CalendarDays,
  Building2,
  Tags,
  Users,
  Settings,
  TrendingUp,
  CalendarCheck,
  UserCircle,
  LogOut,
  ChevronLeft,
  Fuel,
  Truck,
  Wrench,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/constants";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "responsabile", "tecnico"] },
  { to: "/tickets", tid: "ticket", label: "Ticket - Delivery & Assurance", icon: TicketIcon, roles: ["admin", "responsabile", "tecnico"] },
  { to: "/pianificazione", tid: "pianificazione", label: "Pianificazione - Delivery & Assurance", icon: CalendarDays, roles: ["admin", "responsabile", "tecnico"] },
  { to: "/manutenzione", tid: "manutenzione", label: "Ticket - Manutenzione", icon: Wrench, roles: ["admin", "responsabile"] },
  { to: "/risultati", label: "Risultati", icon: TrendingUp, roles: ["admin", "responsabile"] },
  { to: "/presenze", label: "Presenze", icon: CalendarCheck, roles: ["admin", "responsabile"] },
  { to: "/carburante", label: "Carburante", icon: Fuel, roles: ["admin", "responsabile"] },
  { to: "/trasferimenti", label: "Trasferimenti", icon: Truck, roles: ["admin", "responsabile"] },
];

const CONFIG = [
  { to: "/anagrafiche", label: "Anagrafiche", icon: Building2, roles: ["admin", "responsabile"] },
  { to: "/utenti", label: "Utenti", icon: Users, roles: ["admin", "responsabile"] },
];

const ADMIN = [
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
];

const ACCOUNT = [
  { to: "/profilo", label: "Profilo", icon: UserCircle, roles: ["admin", "responsabile", "tecnico"] },
];

function NavItem({ item, collapsed, role }) {
  const location = useLocation();
  if (!item.roles.includes(role)) return null;
  const Icon = item.icon;
  // Dettaglio ticket: evidenzia la sezione di provenienza (Delivery, Pianificazione o Manutenzione)
  const from = location.pathname.startsWith("/tickets/") ? location.state?.from : null;
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${item.tid || item.label.toLowerCase()}`}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
          (from ? from === item.to : isActive)
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        )
      }
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const role = user?.role || "tecnico";
  const showConfig = CONFIG.some((i) => i.roles.includes(role));
  const showAdmin = ADMIN.some((i) => i.roles.includes(role));

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col border-r border-border bg-sidebar transition-all duration-200",
          collapsed ? "w-[76px]" : "w-64"
        )}
      >
        <div className="flex items-center justify-between px-4 py-5">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2.5"
            data-testid="logo-button"
          >
            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg">
              <img src="/logo-sm.png" alt="Powercom" className="h-full w-full object-contain" />
            </span>
            {!collapsed && (
              <span className="text-left leading-none">
                <span className="block text-lg font-extrabold tracking-tight text-navy">FLUX</span>
                <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-wide text-primary">
                  Powercom Workforce Management
                </span>
              </span>
            )}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft size={18} className={cn("transition-transform", collapsed && "rotate-180")} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {NAV.map((i) => (
            <NavItem key={i.to} item={i} collapsed={collapsed} role={role} />
          ))}

          {showConfig && (
            <>
              {!collapsed && <p className="overline px-3 pb-1 pt-5">Configurazione</p>}
              {collapsed && <div className="my-3 border-t border-border" />}
              {CONFIG.map((i) => (
                <NavItem key={i.to} item={i} collapsed={collapsed} role={role} />
              ))}
            </>
          )}

          {showAdmin && (
            <>
              {!collapsed && <p className="overline px-3 pb-1 pt-5">Amministrazione</p>}
              {collapsed && <div className="my-3 border-t border-border" />}
              {ADMIN.map((i) => (
                <NavItem key={i.to} item={i} collapsed={collapsed} role={role} />
              ))}
            </>
          )}

          <>
            {!collapsed && <p className="overline px-3 pb-1 pt-5">Account</p>}
            {collapsed && <div className="my-3 border-t border-border" />}
            {ACCOUNT.map((i) => (
              <NavItem key={i.to} item={i} collapsed={collapsed} role={role} />
            ))}
          </>
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy text-sm font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </span>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-navy">{user?.name}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {ROLE_LABEL[role]}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-destructive transition-colors hover:bg-red-50"
          >
            <LogOut size={18} />
            {!collapsed && "Esci"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="w-full px-6 py-6 md:px-10">{children}</div>
      </main>
    </div>
  );
}
